<template>
    <div v-if="loaded">
        <rkm-form
            ref="Form"
            show-legend
        >
            <rkm-row>
                <rkm-col cols="9">
                    <rkm-form-select
                        :disabled="model.exportado"
                        :options="form.unidades"
                        :validations="validations.unidade"
                        label="Unidade"
                        show-default-option
                        text-field="nomeFantasia"
                        v-model="model.unidadeId"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-select
                        :disabled="model.exportado || model.procedimentos.length > 0"
                        :options="form.competencias"
                        :validations="validations.competencia"
                        label="Competência"
                        show-default-option
                        text-field="valor"
                        v-model="model.competenciaId"
                        value-field="id"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="3">
                    <rkm-form-date-picker
                        :disabled="model.exportado || !model.competenciaId || model.procedimentos.length > 0"
                        :max-date="maxDateAdmissao"
                        :validations="validations.dataAdmissao"
                        label="Data de admissão"
                        right
                        v-model="model.dataAdmissao"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-date-picker
                        :disabled="model.exportado || !model.competenciaId || model.procedimentos.length > 0"
                        :min-date="minDateConclusao"
                        :max-date="additionals.dataAtual"
                        :validations="validations.dataConclusao"
                        label="Data da conclusão"
                        right
                        v-model="model.dataConclusao"
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-select
                        :disabled="model.exportado"
                        :options="form.origensPaciente"
                        :validations="validations.origemPaciente"
                        label="Origem do paciente"
                        show-default-option
                        text-field="nome"
                        v-model="model.origemPacienteId"
                        value-field="id"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-area
                        title="Paciente"
                        type="fieldset"
                    >
                        <rkm-row>
                            <rkm-col cols="6">
                                <rkm-form-input-autocomplete
                                    :disabled="model.exportado || model.procedimentos.length > 0"
                                    :repository="repositories.paciente"
                                    :request-query-string-params="{ with: ['cidadao.paisNascimento', 'cadastroIndividual']} "
                                    :search-fields="fields.autocomplete.paciente"
                                    :text-field="p => p.cidadao.nome"
                                    :validations="validations.paciente"
                                    value-field="id"
                                    label="Nome"
                                    v-model="model.paciente"
                                >
                                    <template #itemLabel="{ item }">
                                        {{ item.cidadao.nome }}
                                        <template v-if="item.cidadao.cns">
                                            - {{ formatCns(item.cidadao.cns) }}
                                        </template>
                                    </template>
                                </rkm-form-input-autocomplete>
                            </rkm-col>

                            <rkm-col cols="3">
                                <rkm-form-input
                                    :value="getSafely(() => model.paciente.cidadao.dataNascimento
                                        ? others.moment(model.paciente.cidadao.dataNascimento).format('DD/MM/YYYY')
                                        : '')"
                                    disabled
                                    label="Data de nascimento"
                                />
                            </rkm-col>

                            <rkm-col cols="3">
                                <rkm-form-input
                                    :value="getSafely(() => model.paciente.prontuario)"
                                    disabled
                                    label="Prontuário"
                                />
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col cols="3">
                                <rkm-form-input
                                    :value="getSafely(() => model.paciente.cidadao.cns ? formatCns(model.paciente.cidadao.cns) : '')"
                                    disabled
                                    label="CNS"
                                />
                            </rkm-col>

                            <rkm-col cols="3">
                                <rkm-form-input
                                    :value="getSafely(() => model.paciente.cidadao.cpf ? formatCpf(model.paciente.cidadao.cpf) : '')"
                                    disabled
                                    label="CPF"
                                />
                            </rkm-col>

                            <rkm-col cols="6">
                                <rkm-form-input
                                    :value="getSafely(() => model.paciente.cidadao.nomeMae)"
                                    disabled
                                    label="Nome da mãe"
                                />
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col cols="3">
                                <rkm-form-input
                                    :value="getSafely(() => model.paciente.cidadao.sexo.descricao)"
                                    disabled
                                    label="Sexo"
                                />
                            </rkm-col>

                            <rkm-col cols="3">
                                <rkm-form-input
                                    :value="getSafely(() => model.paciente.cidadao.racaCor.descricao)"
                                    disabled
                                    label="Raça/cor"
                                />
                            </rkm-col>

                            <rkm-col cols="6">
                                <rkm-form-input
                                    :value="getSafely(() => model.paciente.cidadao.etniaIndigena.descricao)"
                                    disabled
                                    label="Etnia indígena"
                                />
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col cols="9">
                                <rkm-form-input
                                    :value="getSafely(() => model.paciente.cidadao.domicilio.endereco.completo)"
                                    disabled
                                    label="Endereço"
                                />
                            </rkm-col>

                            <rkm-col cols="3">
                                <rkm-form-input
                                    :value="getSafely(() => model.paciente.cidadao.domicilio.endereco.logradouro.cep
                                        ? formatCep(model.paciente.cidadao.domicilio.endereco.logradouro.cep)
                                        : '')"
                                    disabled
                                    label="CEP"
                                />
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col cols="3">
                                <rkm-form-input
                                    :value="getSafely(() => model.paciente.cidadao.paisNascimento.nome)"
                                    disabled
                                    label="País de nacionalidade"
                                />
                            </rkm-col>

                            <rkm-col cols="6">
                                <rkm-form-input
                                    :value="getSafely(() => model.paciente.email)"
                                    disabled
                                    label="E-mail"
                                />
                            </rkm-col>

                            <rkm-col cols="3">
                                <rkm-form-input
                                    :value="getSafely(() => model.paciente.cidadao.telefonePrincipal
                                        ? formatPhoneOrCellPhone(model.paciente.cidadao.telefonePrincipal.numero)
                                        : '')"
                                    disabled
                                    label="Telefone"
                                />
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col cols="3">
                                <rkm-form-input
                                    :value="getSafely(() => model.paciente.cadastroIndividual.situacaoRuaId ? 'SIM' : 'NÃO')"
                                    disabled
                                    label="Em situação de rua?"
                                />
                            </rkm-col>

                            <rkm-col>
                                <rkm-form-input
                                    :value="getSafely(() => model.paciente.cadastroIndividual.usaAlcool
                                        || model.paciente.cadastroIndividual.outrasDrogas
                                        ? ('SIM - '
                                            + (model.paciente.cadastroIndividual.usaAlcool ? 'ÁLCOOL, ' : '')
                                            + (model.paciente.cadastroIndividual.outrasDrogas ? 'OUTRAS DROGAS' : '')
                                        )
                                        : 'NÃO'
                                    )"
                                    disabled
                                    label="Usuário de drogas?"
                                />
                            </rkm-col>
                        </rkm-row>
                    </rkm-area>
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="auto">
                    <rkm-form-radio-group
                        :disabled="model.exportado"
                        label="Cobertura de estratégia saúde da família?"
                        v-model="model.coberturaEstrategiaSaudeFamilia"
                    >
                        <rkm-form-radio :value="true">Sim</rkm-form-radio>
                        <rkm-form-radio :value="false">Não</rkm-form-radio>
                    </rkm-form-radio-group>
                </rkm-col>

                <rkm-col>
                    <rkm-form-select
                        :disabled="model.exportado || !model.coberturaEstrategiaSaudeFamilia"
                        :options="form.unidades"
                        :validations="validations.unidadeCoberturaEstrategiaFamiliaId"
                        label="Unidade da cobertura de ESF"
                        show-default-option
                        text-field="nomeFantasia"
                        v-model="model.unidadeCoberturaEstrategiaFamiliaId"
                        value-field="id"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-input-autocomplete
                        :disabled="model.exportado || !model.competenciaId"
                        :repository="repositories.cid"
                        :request-query-string-params="{ principal: true, competencia_id: model.competenciaId }"
                        :search-fields="fields.autocomplete.cid"
                        :text-field="c => `${formatCid(c.codigo)} - ${c.nome}`"
                        :validations="validations.cidPrincipal"
                        label="CID principal"
                        v-model="model.cidPrincipal"
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-input-autocomplete
                        :disabled="model.exportado || !model.competenciaId"
                        :repository="repositories.cid"
                        :request-query-string-params="{ principal: false, competencia_id: model.competenciaId }"
                        :search-fields="fields.autocomplete.cid"
                        :text-field="item => `${formatCid(item.codigo)} - ${item.nome}`"
                        :validations="validations.cidSecundario1"
                        label="CID secundário 1"
                        v-model="model.cidSecundario1"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-input-autocomplete
                        :disabled="model.exportado || !model.competenciaId"
                        :repository="repositories.cid"
                        :request-query-string-params="{ principal: false, competencia_id: model.competenciaId}"
                        :search-fields="fields.autocomplete.cid"
                        :text-field="item => `${formatCid(item.codigo)} - ${item.nome}`"
                        :validations="validations.cidSecundario2"
                        label="CID secundário 2"
                        v-model="model.cidSecundario2"
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-input-autocomplete
                        :disabled="model.exportado || !model.competenciaId"
                        :repository="repositories.cid"
                        :request-query-string-params="{ principal: false, competencia_id: model.competenciaId }"
                        :search-fields="fields.autocomplete.cid"
                        :text-field="item => `${formatCid(item.codigo)} - ${item.nome}`"
                        :validations="validations.cidSecundario3"
                        label="CID secundário 3"
                        v-model="model.cidSecundario3"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-input-autocomplete
                        :disabled="model.exportado || !model.competenciaId"
                        :repository="repositories.cid"
                        :request-query-string-params="{ principal: true, competencia_id: model.competenciaId }"
                        :search-fields="fields.autocomplete.cid"
                        :text-field="c => `${formatCid(c.codigo)} - ${c.nome}`"
                        :validations="validations.cidCausaAssociada"
                        label="CID causas associadas"
                        v-model="model.cidCausaAssociada"
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-select
                        :disabled="model.exportado"
                        :options="form.destinosPaciente"
                        :validations="validations.destinoPaciente"
                        label="Destino do paciente"
                        show-default-option
                        text-field="nome"
                        v-model="model.destinoPacienteId"
                        value-field="id"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-select
                        :disabled="model.exportado"
                        :options="form.motivosSaidaPermanencia"
                        :validations="validations.motivoSaidaPermanencia"
                        label="Motivo de saída/permanência"
                        show-default-option
                        text-field="nome"
                        v-model="model.motivoSaidaPermanenciaId"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-date-picker
                        :disabled="model.exportado"
                        :min-date="model.dataAdmissao
                            ? others.moment(model.dataAdmissao).format('DD/MM/YYYY')
                            : null"
                        :max-date="model.dataConclusao
                            ? others.moment(model.dataConclusao).format('DD/MM/YYYY')
                            : additionals.dataAtual"
                        :validations="validations.dataOcorrencia"
                        label="Data da ocorrência (alta, transferência ou óbito)"
                        right
                        v-model="model.dataOcorrencia"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="9">
                    <rkm-form-select
                        :disabled="model.exportado"
                        :options="form.carateresAtendimento"
                        :validations="validations.caraterAtendimento"
                        label="Caráter do atendimento"
                        show-default-option
                        text-field="nome"
                        v-model="model.caraterAtendimentoId"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-input
                        :disabled="model.exportado"
                        :validations="validations.numeroAutorizacao"
                        label="Número da autorização"
                        number
                        v-model="model.numeroAutorizacao"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <Procedimento
            :data="{ additionals, form, model }"
            ref="Procedimento"
        />
    </div>
</template>

<script>
// Components.
import Procedimento from './components/procedimento/Index'

// Mixins.
import { repository, error, formats, object, data, stringCase } from '@/mixins'

// Vuex.
import { mapActions } from 'vuex'

// Moment.
import moment from 'moment'

// Enums.
import procedimento from '@/enums/sigtap/procedimento'

export default {
    components: { Procedimento },
    computed: {
        fields() {

            return {
                autocomplete: {
                    paciente: [
                        { key: 'cidadao.nome', searchable: true },
                    ],
                    cid: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                }
            }
        },
        validations() {

            return {
                unidade: { required: true },
                competencia: { required: true },
                paciente: { required: true },
                dataAdmissao: { required: true },
                origemPaciente: { required: true },
                cidPrincipal: { required: true },
                cidSecundario1: {},
                cidSecundario2: {},
                cidSecundario3: {},
                cidCausaAssociada: {},
                unidadeCoberturaEstrategiaFamiliaId: { required: this.model.coberturaEstrategiaSaudeFamilia },
                destinoPaciente: { required: true },
                motivoSaidaPermanencia: { required: true },
                dataOcorrencia: {},
                dataConclusao: {},
                caraterAtendimento: { required: true },
                numeroAutorizacao: { required: this.hasProcedimentoRaasQuePrecisaDeAutorizacao, max: 13 },
            }
        },
        hasProcedimentoRaasQuePrecisaDeAutorizacao() {

            const procedimentos = [
                this.enums.procedimento.ACOMPANHAMENTO_DE_PACIENTE_EM_SAUDE_MENTAL,
                this.enums.procedimento.ACOMPANHAMENTO_DE_PESSOAS_COM_NECESSIDADES_DECORRENTES_DO_USO_DE_ALCOOL_CRACK_E_OUTRAS_DROGAS,
                this.enums.procedimento.ACOMPANHAMENTO_DE_PESSOAS_ADULTAS_COM_SOFRIMENTO_OU_TRANSTORNOS_MENTAIS_DECORRENTES_DO_USO_DE_CRACK_ALCOOL_E_OUTRAS_DROGAS,
                this.enums.procedimento.ACOMPANHAMENTO_DA_POPULACAO_INFANTO_JUVENIL_COM_SOFRIMENTO_OU_TRANSTORNOS_MENTAIS_DECORRENTES_DO_USO_DE_CRACK_ALCOOL_E_OUTRAS_DROGAS,
            ]

            return this.model.procedimentos.some(p => procedimentos.includes(p.procedimento.codigo))
        },
        maxDateAdmissao() {

            if (!this.model.competenciaId) {

                return null
            }

            const dataAtual = this.others.moment(this.additionals.dataAtual, 'DD/MM/YYYY')
            let date = this.model.dataConclusao || dataAtual

            date = date > this.competencia.fim ? this.competencia.fim : date

            return this.others.moment(date).format('DD/MM/YYYY')
        },
        minDateConclusao() {

            if (!this.model.competenciaId) {

                return null
            }

            const date = this.model.dataAdmissao && this.model.dataAdmissao > this.competencia.inicio
                ? this.model.dataAdmissao
                : this.competencia.inicio

            return this.others.moment(date).format('DD/MM/YYYY')
        },
        competencia() {

            if (!this.model.competenciaId) {

                return null
            }

            const competencia = this.form.competencias.find(c => c.id === this.model.competenciaId).valor
            const ano = parseInt(competencia.substring(0, 4))
            const mes = parseInt(competencia.substring(4, 6))

            return {
                inicio: this.others.moment([ano, mes - 1]).startOf('month').toDate(),
                fim: this.others.moment([ano, mes - 1]).endOf('month').toDate()
            }
        }
    },
    created() {

        this.repositories = {
            procedimentoRaas: this.getConectaSusRepository('raas.procedimento'),
            paciente: this.getConectaSusRepository('cadastro.sujeitoAtencao.paciente'),
            cid: this.getConectaSusRepository('sigtap.cid'),
        }

        this.load()
    },
    data: () => ({
        loaded: false,
        additionals: null,
        form: null,
        model: null,
        enums: { procedimento },
        others: { moment }
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.setLoading()

            const repository = this.id
                ? this.repositories.procedimentoRaas.edit(this.id)
                : this.repositories.procedimentoRaas.create({ origem: 'cadastro' })

            repository
                .then(({ data }) => {

                    this.formatModelDates(data.model)

                    this.additionals = data.additionals
                    this.form = data.form
                    this.model = data.model

                    this.addWatchers()

                    this.$emit('model-load', this.model)

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        formatModelDates(model) {

            if (model.dataAdmissao) {

                model.dataAdmissao = this.others.moment(model.dataAdmissao, 'DD/MM/YYYY').toDate()
            }

            if (model.dataConclusao) {

                model.dataConclusao = this.others.moment(model.dataConclusao, 'DD/MM/YYYY').toDate()
            }

            if (model.dataOcorrencia) {

                model.dataOcorrencia = this.others.moment(model.dataOcorrencia, 'DD/MM/YYYY').toDate()
            }

            model.procedimentos.map(procedimento => {

                procedimento.dataExecucao = this.others.moment(procedimento.dataExecucao, 'DD/MM/YYYY').toDate()
            })
        },
        addWatchers() {

            this.$watch('model.competenciaId', () => {

                this.model.dataAdmissao = null
                this.model.dataConclusao = null
                this.model.cidPrincipal = null
                this.model.cidSecundario1 = null
                this.model.cidSecundario2 = null
                this.model.cidSecundario3 = null
                this.model.cidCausaAssociada = null
            })

            this.$watch('model.dataAdmissao', () => {

                this.model.dataOcorrencia = null
            })

            this.$watch('model.dataConclusao', () => {

                this.model.dataOcorrencia = null
            })

            this.$watch('model.coberturaEstrategiaSaudeFamilia', () => {

                this.model.unidadeCoberturaEstrategiaFamiliaId = null
            })
        },
        save() {

            Promise.all([
                this.$refs.Form.validate(),
                this.$refs.Procedimento.validate(),
            ])
                .then(([formValid, procedimentosValid]) => {

                    if (!formValid || !procedimentosValid) return

                    this.setLoading()

                    const repository = this.id
                        ? this.repositories.procedimentoRaas.update(this.id, this.prepareData())
                        : this.repositories.procedimentoRaas.store(this.prepareData())

                    repository
                        .then(() => {

                            this.showSaveNotification()

                            this.$emit('save')
                        })
                        .catch(this.handleError)
                        .finally(() => this.unsetLoading())
                })
        },
        prepareData() {

            const data = this.clone(this.model)

            data.origem = 'cadastro'

            delete data.exportado

            this.createIdKeysToObject(data, [
                'paciente',
                'cidPrincipal',
                'cidSecundario1',
                'cidSecundario2',
                'cidSecundario3',
                'cidCausaAssociada',
            ])

            this.formateDates(data, [
                'dataAdmissao',
                'dataConclusao',
                'dataOcorrencia',
            ], 'DD/MM/YYYY')

            data.procedimentos.map(procedimento => {

                if (!procedimento.saved) {

                    procedimento.id = null
                }

                this.createIdKeysToObject(procedimento, [
                    'usuario',
                    'ocupacao',
                    'procedimento',
                    'servico',
                    'classificacao',
                ])

                this.formateDates(procedimento, [
                    'dataExecucao',
                ], 'DD/MM/YYYY')

                delete procedimento.saved
            })

            return this.camelObjectKeysToSnakeRecursive(data)
        }
    },
    mixins: [repository, error, formats, object, data, stringCase],
    props: {
        id: { type: Number, default: null }
    },
}
</script>